/* eslint-disable array-bracket-newline */
import { Chain, Connector, ConnectorData, mainnet } from 'wagmi';
import TrezorConnect from '@trezor/connect-web';
import type { TrezorConnect as TrezorConnectType } from '@trezor/connect-web';
import {
  WalletsName,
  WalletsType,
} from '../components/ConnectedWallet/wallets.ls';
import { AuthAPI } from '../store/createStore';

const firstEthWallet = 'm/44\'/60\'/0\'/0/0';

export class TrezorConnector extends Connector<any> {
  readonly id = WalletsType.TREZOR;
  readonly name = WalletsName.TREZOR;
  readonly ready = true;

  provider: TrezorConnectType;
  signer: any;

  constructor(chains: Chain[]) {
    super({ chains, options: {} });

    this.provider = TrezorConnect;

    this.provider.init({
      lazyLoad: true, // this param will prevent iframe injection until TrezorConnect.method will be called
      manifest: {
        email: 'riabkoleksandr@kattana.io',
        appUrl: 'https://app.kattana.io',
      },
    });

    this.onDisconnect = this.onDisconnect.bind(this);
  }

  async getProvider() {
    return this.provider;
  }

  async connect(
    config?: { chainId?: number | undefined } | undefined
  ): Promise<Required<ConnectorData>> {
    this.emit('message', { type: 'connecting' });

    this.provider.on('device-disconnect', this.onDisconnect);

    try {
      const { ok, token } = await AuthAPI.generate();
      if (!ok || !token) {
        throw new Error('Trezor Suite challenge not generated');
      }
      const data = await this.provider.requestLogin({
        challengeHidden: token,
        challengeVisual: 'Login to',
      });
      if (!data.success) {
        throw new Error('Failed to connect trezor wallet');
      }
      const addressData = await this.provider.ethereumGetAddress({
        path: firstEthWallet, // first ethereum account
      });
      if (!addressData.success) {
        throw new Error('Failed to find Etherium account');
      }
      return {
        account: addressData.payload.address,
        chain: {
          id: mainnet.id,
          unsupported: false,
        },
        provider: this.provider,
      } as Required<ConnectorData>;
    } catch (e: any) {
      throw new Error(e.message);
    }
  }

  // No possible ways to programmatically disconnect trezor suit
  async disconnect(): Promise<void> {
    console.log('Tron wallet disconnected');
    window.removeEventListener('message', this.onDisconnect);
  }

  async getAccount(): Promise<`0x${string}`> {
    const addressData = await this.provider.ethereumGetAddress({
      path: firstEthWallet,
    });
    if (!addressData.success) {
      throw new Error('Failed to find Etherium account');
    }
    return addressData.payload.address as `0x${string}`;
  }

  async getChainId(): Promise<number> {
    return mainnet.id;
  }

  async getWalletClient(
    config?: { chainId?: number | undefined } | undefined
  ): Promise<any> {
    const provider = this.provider;
    return {
      async signTypedData({ domain, message, primaryType, types }: any) {
        const eip712Data = {
          types: {
            EIP712Domain: [
              {
                name: 'name',
                type: 'string',
              },
              {
                name: 'version',
                type: 'string',
              },
            ],
            ...types,
          },
          primaryType,
          domain,
          message,
        };

        try {
          const signature = await provider.ethereumSignTypedData({
            path: firstEthWallet,
            data: eip712Data,
            metamask_v4_compat: true,
          });

          if (!signature.success) {
            throw new Error('Could not sign message');
          }
          return signature.payload.signature;
        } catch (e: any) {
          throw new Error(e);
        }
      },
    };
  }

  async isAuthorized(): Promise<boolean> {
    const { success } = await this.provider.getAccountInfo({
      path: firstEthWallet,
      coin: 'ETH',
    });
    return success;
  }

  protected onAccountsChanged(accounts: `0x${string}`[]): void {
    throw new Error('Method onAccountsChanged not implemented.');
  }

  protected onChainChanged(chain: string | number): void {
    throw new Error('Method onChainChanged not implemented.');
  }

  protected onDisconnect(e: any) {
    this.emit('disconnect');
  }
}
